import { Zap, Palette } from 'lucide-react';
import { cn, getNameInitials } from '@/lib/utils';
import type { StudentAchiever, StudentOverview } from '@/types/student';

/**
 * How this child's class is standing.
 *
 * The one place in the student panel that names another child, and deliberately
 * the narrowest version of that the design can be drawn from: their own class,
 * a name, a section and a mean percentage. No roll number, no admission number,
 * no photograph and no per-paper marks — the server sends none of them.
 *
 * Each circle holds the child's initials for that reason: a real photograph of
 * one child shown to another is personal data this screen has no need for
 * (CLAUDE.md §9). It used to hold one stock face for everybody, which read as
 * every topper's photograph.
 */

/**
 * The initials that stand in for a face. Hidden from screen readers — the full
 * name is printed right beside it.
 */
function InitialsAvatar({ fullName, className }: { fullName: string; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'grid place-items-center rounded-full bg-white font-black text-slate-800 select-none',
        className,
      )}
    >
      {getNameInitials(fullName)}
    </span>
  );
}

interface TopStudentsSectionProps {
  leaderboard: StudentOverview['leaderboard'] | null;
}

export function TopStudentsSection({ leaderboard }: TopStudentsSectionProps) {
  const top = leaderboard?.top ?? [];
  const rankOf = (rank: number): StudentAchiever | undefined =>
    top.find((student) => student.rank === rank);

  const rank1 = rankOf(1);
  const rank2 = rankOf(2);
  const rank3 = rankOf(3);
  const rank4 = rankOf(4);
  const rank5 = rankOf(5);
  const activityTopper = leaderboard?.activityTopper ?? null;

  return (
    <div className="rounded-[24px] border border-[#e2e4f0] bg-white p-5 shadow-[0_6px_20px_rgba(40,42,85,.05)] dark:border-white/10 dark:bg-[#1b1d34]">
      {/* Section Header */}
      <div className="flex items-center justify-between border-b border-[#e9e9f0] pb-3 mb-2 dark:border-white/10">
        <h3 className="text-[14px] font-black text-[#1b2142] dark:text-[#f2f4ff] flex items-center gap-2.5">
          <span className="grid h-9 w-9 place-items-center rounded-[14px] bg-amber-100/80 border border-amber-300/80 p-1 shadow-2xs dark:bg-amber-500/20 dark:border-amber-400/40">
            <img
              src="/student-overview/grade-illustration.png"
              alt="Bright Cup"
              className="h-7 w-7 object-contain animate-gold-shine"
            />
          </span>{' '}
          Class Academic Leaderboard
        </h3>
        
      </div>

      {/* Nothing has been marked in this class yet. Said plainly rather than
          drawn as three empty podium cards. */}
      {top.length === 0 && !activityTopper ? (
        <p className="py-8 text-center text-sm font-semibold text-[#7d8198] dark:text-[#a0a5bd]">
          No papers have been marked in your class yet. The leaderboard appears once your teachers
          publish the first results.
        </p>
      ) : (
        /* Grid Layout: 3 Overlapping Gamer Cards on Left + Compact Sub-Achievers List on Right */
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-center">
          {/* LEFT SIDE: 3 Overlapping Gamer-Style Solid Color Cards */}
          <div className="lg:col-span-8 flex flex-col sm:flex-row items-center justify-center gap-3.5 sm:gap-0 sm:-space-x-3 py-1">
            {/* RANK 3 CARD (Vibrant Magenta/Pink Solid Card - Left on desktop, 3rd on mobile) */}
            {rank3 && (
              <div className="order-3 sm:order-1 group relative z-10 w-full sm:w-[34%] rounded-[28px] bg-gradient-to-b from-[#e91e63] to-[#c2185b] p-5 text-white shadow-lg transition-all duration-300 hover:z-30 hover:scale-105 flex flex-col items-center text-center min-h-[220px] sm:min-h-[240px] justify-between border-2 border-pink-300/30">
                <div className="relative mt-1">
                  <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-white p-1 ring-4 ring-[#81d4fa] shadow-md group-hover:scale-105 transition-transform overflow-hidden">
                    <InitialsAvatar fullName={rank3.fullName} className="h-full w-full text-lg sm:text-xl" />
                  </div>
                  {/* Ribbon Badge 3 Pinned at Bottom Right */}
                  <div className="absolute -bottom-1 -right-1 z-30 flex flex-col items-center">
                    <div className="grid h-6 w-6 sm:h-7 sm:w-7 place-items-center rounded-full bg-[#d84315] border-2 border-white text-white font-black text-2xs sm:text-xs shadow-md leading-none">
                      3
                    </div>
                    <div className="flex gap-0.5 -mt-1">
                      <span className="h-2.5 w-1 bg-emerald-500 rounded-b-xs transform -rotate-12" />
                      <span className="h-2.5 w-1 bg-emerald-500 rounded-b-xs transform rotate-12" />
                    </div>
                  </div>
                </div>

                <div className="my-2">
                  <h4 className="font-black text-sm text-white leading-tight tracking-wide uppercase">
                    {rank3.fullName}
                  </h4>
                  <p className="text-[10px] text-white/80 font-bold mt-0.5">{rank3.displayName}</p>
                </div>

                <div className="inline-flex items-center gap-1 rounded-full bg-black/25 px-3 py-1 text-xs font-black text-yellow-300 shadow-inner">
                  <Zap className="h-3.5 w-3.5 fill-yellow-300 text-yellow-300" />
                  <span>{rank3.percentage}% Score</span>
                </div>
              </div>
            )}

            {/* RANK 1 CARD (Royal Blue Solid Card - Center Spotlight on desktop, 1st on mobile) */}
            {rank1 && (
              <div className="order-1 sm:order-2 group relative z-20 w-full sm:w-[38%] rounded-[32px] bg-gradient-to-b from-[#1e88e5] to-[#1565c0] p-5 sm:p-6 text-white shadow-[0_10px_30px_rgba(21,101,192,.35)] transition-all duration-300 hover:z-30 hover:scale-105 flex flex-col items-center text-center min-h-[250px] sm:min-h-[275px] justify-between border-4 border-white">
                <div className="relative mt-1">
                  <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-full bg-white p-1 ring-4 ring-[#81d4fa] shadow-lg group-hover:scale-105 transition-transform overflow-hidden">
                    <InitialsAvatar fullName={rank1.fullName} className="h-full w-full text-xl sm:text-2xl" />
                  </div>
                  {/* Ribbon Badge 1 Pinned at Bottom Right */}
                  <div className="absolute -bottom-1 -right-1 z-30 flex flex-col items-center">
                    <div className="grid h-7 w-7 sm:h-8 sm:w-8 place-items-center rounded-full bg-[#ffc107] border-2 border-white text-[#1a1000] font-black text-xs sm:text-sm shadow-xl leading-none">
                      1
                    </div>
                    <div className="flex gap-0.5 -mt-1">
                      <span className="h-3 w-1 bg-emerald-500 rounded-b-xs transform -rotate-12" />
                      <span className="h-3 w-1 bg-emerald-500 rounded-b-xs transform rotate-12" />
                    </div>
                  </div>
                </div>

                <div className="my-2">
                  <h4 className="font-black text-sm sm:text-base text-white leading-tight tracking-wide">
                    {rank1.fullName}
                  </h4>
                  <p className="text-[11px] text-sky-100 font-bold mt-0.5">{rank1.displayName}</p>
                </div>

                <div className="inline-flex items-center gap-1 rounded-full bg-black/25 px-4 py-1.5 text-xs sm:text-sm font-black text-yellow-300 shadow-inner">
                  <Zap className="h-4 w-4 fill-yellow-300 text-yellow-300" />
                  <span>{rank1.percentage}% Score</span>
                </div>
              </div>
            )}

            {/* RANK 2 CARD (Emerald Green Solid Card - Right on desktop, 2nd on mobile) */}
            {rank2 && (
              <div className="order-2 sm:order-3 group relative z-10 w-full sm:w-[34%] rounded-[28px] bg-gradient-to-b from-[#43a047] to-[#2e7d32] p-5 text-white shadow-lg transition-all duration-300 hover:z-30 hover:scale-105 flex flex-col items-center text-center min-h-[220px] sm:min-h-[240px] justify-between border-2 border-green-300/30">
                <div className="relative mt-1">
                  <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-white p-1 ring-4 ring-[#81d4fa] shadow-md group-hover:scale-105 transition-transform overflow-hidden">
                    <InitialsAvatar fullName={rank2.fullName} className="h-full w-full text-lg sm:text-xl" />
                  </div>
                  {/* Ribbon Badge 2 Pinned at Bottom Right */}
                  <div className="absolute -bottom-1 -right-1 z-30 flex flex-col items-center">
                    <div className="grid h-6 w-6 sm:h-7 sm:w-7 place-items-center rounded-full bg-[#e2e8f0] border-2 border-white text-[#0f172a] font-black text-2xs sm:text-xs shadow-md leading-none">
                      2
                    </div>
                    <div className="flex gap-0.5 -mt-1">
                      <span className="h-2.5 w-1 bg-emerald-500 rounded-b-xs transform -rotate-12" />
                      <span className="h-2.5 w-1 bg-emerald-500 rounded-b-xs transform rotate-12" />
                    </div>
                  </div>
                </div>

                <div className="my-2">
                  <h4 className="font-black text-sm text-white leading-tight tracking-wide">
                    {rank2.fullName}
                  </h4>
                  <p className="text-[10px] text-white/80 font-bold mt-0.5">{rank2.displayName}</p>
                </div>

                <div className="inline-flex items-center gap-1 rounded-full bg-black/25 px-3 py-1 text-xs font-black text-yellow-300 shadow-inner">
                  <Zap className="h-3.5 w-3.5 fill-yellow-300 text-yellow-300" />
                  <span>{rank2.percentage}% Score</span>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT SIDE: Compact 4th Topper, 5th Topper & Activity Topper List (Decreased Gaps) */}
          <div className="lg:col-span-4 flex flex-col justify-center gap-1.5 border-t lg:border-t-0 lg:border-l border-[#e9e9f0] pt-4 lg:pt-0 pl-0 lg:pl-4 dark:border-white/10 mt-2 lg:mt-0">
            <span className="text-[10px] uppercase tracking-wider font-black text-[#7d8198] dark:text-[#a0a5bd] mb-0.5">
              Sub-Achievers & Activity Topper
            </span>

            {/* 4th and 5th Topper Items */}
            {[rank4, rank5].map((student, index) =>
              student ? (
                <div
                  key={student.rank}
                  className="rounded-[12px] border border-slate-200 bg-[#f8fafc] p-2 flex items-center justify-between transition-all hover:translate-x-1 dark:border-slate-700 dark:bg-slate-900/60"
                >
                  <div className="flex items-center gap-2">
                    <InitialsAvatar
                      fullName={student.fullName}
                      className="h-7 w-7 shrink-0 text-2xs ring-2 ring-slate-300"
                    />
                    <div>
                      <div className="flex items-center pb-1 gap-1">
                        <span className="font-black text-[11px] text-slate-800 dark:text-slate-200 bg-slate-200 dark:bg-slate-700 px-1.5 rounded-pill">
                          {student.rank}th Topper
                        </span>
                        <span className="text-[11px] text-slate-600 dark:text-slate-400 font-bold">
                          {student.displayName}
                        </span>
                      </div>
                      <h5 className="font-bold text-[13px] text-slate-700 dark:text-slate-200">
                        {student.fullName}
                      </h5>
                    </div>
                  </div>
                  <span className="font-black text-2xs text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800 px-2 py-0.5 rounded-pill border border-slate-300 dark:border-slate-600">
                    {student.percentage}%
                  </span>
                </div>
              ) : (
                // Keeps the panel's shape while a small class fills up, rather
                // than letting the row collapse and shift the card beside it.
                <div
                  key={`empty-${index}`}
                  className="rounded-[12px] border border-dashed border-slate-200 bg-[#f8fafc] p-2 text-[11px] font-bold text-slate-400 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-500"
                >
                  Not enough results yet
                </div>
              ),
            )}

            {/* Activity Topper Item */}
            {activityTopper && (
              <div className="rounded-[12px] border border-purple-200 bg-[linear-gradient(135deg,#faf5ff,#f3e8ff)] p-2 flex items-center justify-between transition-all hover:translate-x-1 dark:border-purple-800 dark:bg-[linear-gradient(135deg,#28123d,#180a26)]">
                <div className="flex items-center gap-2">
                  <InitialsAvatar
                    fullName={activityTopper.fullName}
                    className="h-7 w-7 shrink-0 text-2xs ring-2 ring-purple-400"
                  />
                  <div>
                    <div className="flex items-center pb-1 gap-1">
                      <span className="font-black text-[11px] text-purple-950 dark:text-purple-200 bg-purple-200 dark:bg-purple-900 px-1.5 rounded-pill flex items-center gap-0.5">
                        <Palette className="h-2.5 w-2.5 text-purple-700 text-[11px] dark:text-purple-300" />{' '}
                        Activity Topper
                      </span>
                    </div>
                    <h5 className="font-bold text-[13px] text-purple-800 dark:text-purple-200">
                      {activityTopper.fullName} ({activityTopper.activity})
                    </h5>
                  </div>
                </div>
                <span className="font-black text-[9px] text-purple-900 dark:text-purple-200 bg-white dark:bg-purple-950 px-2 py-0.5 rounded-pill border border-purple-300 dark:border-purple-700">
                  {activityTopper.percentage}%
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
